import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import NewsImageUpload from './NewsImageUpload';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'user' | 'admin';
  createdAt: string;
}

interface Product {
  _id: string;
  name: string;
  slug?: string;
  description: string;
  price: number;
  isFree?: boolean;
  category: string;
  imageUrl?: string;
  downloadUrl?: string;
  version: string;
  compatibility: string[];
  tags: string[];
  isActive: boolean;
  createdAt: string;
}

function priceComesFromStripe(product: Product | null): boolean {
  return !!product && !product.isFree && product.price > 0;
}

interface CurrentUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface Subscriber {
  _id: string;
  email: string;
  isConfirmed: boolean;
  isActive: boolean;
  subscribedAt: string;
  confirmedAt?: string;
  unsubscribedAt?: string;
  preferences: {
    newProducts: boolean;
    specialOffers: boolean;
    musicTips: boolean;
    weeklyDigest: boolean;
  };
  source: string;
}

interface NewsTranslation {
  language: 'en' | 'ru';
  title: string;
  subtitle: string;
  content: string;
}

interface NewsItem {
  _id: string;
  slug: string;
  translations: NewsTranslation[];
  imageUrl: string;
  publishedAt: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminPanelC: React.FC = () => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'products' | 'subscribers' | 'news' | 'orders' | 'entitlements'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [entitlements, setEntitlements] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [newsSearch, setNewsSearch] = useState('');
  const [subscriberStatus, setSubscriberStatus] = useState('all');
  const [userPage, setUserPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [subscriberPage, setSubscriberPage] = useState(1);
  const [newsPage, setNewsPage] = useState(1);
  const [userPagination, setUserPagination] = useState({ total: 0, pages: 0 });
  const [productPagination, setProductPagination] = useState({ total: 0, pages: 0 });
  const [subscriberPagination, setSubscriberPagination] = useState({ total: 0, pages: 0 });
  const [newsPagination, setNewsPagination] = useState({ total: 0, pages: 0 });
  const [ordersPagination, setOrdersPagination] = useState({ total: 0, pages: 0 });
  const [entitlementsPagination, setEntitlementsPagination] = useState({ total: 0, pages: 0 });
  const [ordersPage, setOrdersPage] = useState(1);
  const [entitlementsPage, setEntitlementsPage] = useState(1);
  const [ordersFilter, setOrdersFilter] = useState({ status: '', email: '', orderNumber: '' });
  const [entitlementsFilter, setEntitlementsFilter] = useState({ email: '', userId: '', orderId: '', subjectId: '', subjectType: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingNews, setGeneratingNews] = useState(false);

  // Product dialog states
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    category: 'synthesizer',
    imageUrl: '',
    downloadUrl: '',
    version: '1.0.0',
    compatibility: [] as string[],
    tags: [] as string[],
    isActive: true
  });

  // News dialog states
  const [newsDialog, setNewsDialog] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [newsForm, setNewsForm] = useState({
    slug: '',
    imageUrl: '',
    isPublished: true,
    publishedAt: new Date().toISOString().split('T')[0],
    titleEn: '',
    subtitleEn: '',
    contentEn: '',
    titleRu: '',
    subtitleRu: '',
    contentRu: ''
  });

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get('/api/me', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCurrentUser(response.data);
        }
      } catch (error) {
        setError(t('admin.error.failed_load_user_data'));
      }
    };
    fetchCurrentUser();
  }, []);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/admin/users?page=${userPage}&limit=10&search=${userSearch}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users);
        setUserPagination(data.pagination);
      } else {
        setError(data.error || t('admin.error.failed_fetch_users'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/admin/products?page=${productPage}&limit=10&search=${productSearch}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (response.ok) {
        setProducts(data.products);
        setProductPagination(data.pagination);
      } else {
        setError(data.error || t('admin.error.failed_fetch_products'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/newsletter/admin/subscribers?page=${subscriberPage}&limit=10&search=${subscriberSearch}&status=${subscriberStatus}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (response.ok) {
        setSubscribers(data.subscribers);
        setSubscriberPagination(data.pagination);
      } else {
        setError(data.error || t('admin.error.failed_fetch_subscribers'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/news/admin/all?page=${newsPage}&limit=10&search=${newsSearch}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (response.ok) {
        setNews(data.news);
        setNewsPagination(data.pagination);
      } else {
        setError(data.error || t('admin.error.failed_fetch_news'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams({
        page: String(ordersPage),
        limit: '10',
        ...(ordersFilter.status ? { status: ordersFilter.status } : {}),
        ...(ordersFilter.email ? { email: ordersFilter.email } : {}),
        ...(ordersFilter.orderNumber ? { orderNumber: ordersFilter.orderNumber } : {}),
      }).toString();
      const response = await fetch(`/api/admin/orders?${qs}`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (response.ok) {
        setOrders(data.data);
        setOrdersPagination(data.pagination);
      } else {
        setError(data.message || 'Failed to fetch orders');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntitlements = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams({
        page: String(entitlementsPage),
        limit: '10',
        ...(entitlementsFilter.email ? { email: entitlementsFilter.email } : {}),
        ...(entitlementsFilter.userId ? { userId: entitlementsFilter.userId } : {}),
        ...(entitlementsFilter.orderId ? { orderId: entitlementsFilter.orderId } : {}),
        ...(entitlementsFilter.subjectId ? { subjectId: entitlementsFilter.subjectId } : {}),
        ...(entitlementsFilter.subjectType ? { subjectType: entitlementsFilter.subjectType } : {}),
      }).toString();
      const response = await fetch(`/api/admin/entitlements?${qs}`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (response.ok) {
        setEntitlements(data.data);
        setEntitlementsPagination(data.pagination);
      } else {
        setError(data.message || 'Failed to fetch entitlements');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'products') {
        fetchProducts();
      } else if (activeTab === 'subscribers') {
        fetchSubscribers();
      } else if (activeTab === 'news') {
        fetchNews();
      } else if (activeTab === 'orders') {
        fetchOrders();
      } else if (activeTab === 'entitlements') {
        fetchEntitlements();
      }
    }
  }, [currentUser, activeTab, userPage, productPage, subscriberPage, newsPage, ordersPage, entitlementsPage, userSearch, productSearch, subscriberSearch, newsSearch, subscriberStatus, ordersFilter, entitlementsFilter]);

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm(t('admin.confirm.delete_user'))) return;
    
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.user_deleted'));
        fetchUsers();
      } else {
        setError(data.error || t('admin.error.failed_delete_user'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleChangeUserRole = async (userId: string, newRole: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role: newRole })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.user_role_changed'));
        fetchUsers();
      } else {
        setError(data.error || t('admin.error.failed_change_role'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleProductSubmit = async () => {
    try {
      const url = editingProduct ? `/api/admin/products/${editingProduct._id}` : '/api/admin/products';
      const method = editingProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(productForm)
      });
      
      const data = await response.json();
      if (response.ok) {
        setSuccess(editingProduct ? t('admin.success.product_updated') : t('admin.success.product_created'));
        setProductDialog(false);
        resetProductForm();
        fetchProducts();
      } else {
        setError(data.error || t('admin.error.failed_create_product'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm(t('admin.confirm.delete_product'))) return;
    
    try {
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.product_deleted'));
        fetchProducts();
      } else {
        setError(data.error || t('admin.error.failed_delete_product'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const openProductDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        imageUrl: product.imageUrl || '',
        downloadUrl: product.downloadUrl || '',
        version: product.version,
        compatibility: product.compatibility,
        tags: product.tags,
        isActive: product.isActive
      });
    } else {
      setEditingProduct(null);
      resetProductForm();
    }
    setProductDialog(true);
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price: 0,
      category: 'synthesizer',
      imageUrl: '',
      downloadUrl: '',
      version: '1.0.0',
      compatibility: [],
      tags: [],
      isActive: true
    });
  };

  const handleNewsSubmit = async () => {
    try {
      const newsData = {
        slug: newsForm.slug,
        imageUrl: newsForm.imageUrl,
        isPublished: newsForm.isPublished,
        publishedAt: new Date(newsForm.publishedAt).toISOString(),
        translations: [
          {
            language: 'en',
            title: newsForm.titleEn,
            subtitle: newsForm.subtitleEn,
            content: newsForm.contentEn
          },
          ...(newsForm.titleRu ? [{
            language: 'ru',
            title: newsForm.titleRu,
            subtitle: newsForm.subtitleRu,
            content: newsForm.contentRu
          }] : [])
        ]
      };

      const url = editingNews ? `/api/news/admin/${editingNews._id}` : '/api/news/admin';
      const method = editingNews ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(newsData)
      });
      
      const data = await response.json();
      if (response.ok) {
        setSuccess(editingNews ? t('admin.success.news_updated') : t('admin.success.news_created'));
        setNewsDialog(false);
        resetNewsForm();
        fetchNews();
      } else {
        setError(data.error || t('admin.error.failed_create_news'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    if (!window.confirm(t('admin.confirm.delete_news'))) return;
    
    try {
      const response = await fetch(`/api/news/admin/${newsId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.news_deleted'));
        fetchNews();
      } else {
        setError(data.error || t('admin.error.failed_delete_news'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const openNewsDialog = (newsItem?: NewsItem) => {
    if (newsItem) {
      setEditingNews(newsItem);
      const enTranslation = newsItem.translations.find(t => t.language === 'en');
      const ruTranslation = newsItem.translations.find(t => t.language === 'ru');
      
      setNewsForm({
        slug: newsItem.slug,
        imageUrl: newsItem.imageUrl,
        isPublished: newsItem.isPublished,
        publishedAt: new Date(newsItem.publishedAt).toISOString().split('T')[0],
        titleEn: enTranslation?.title || '',
        subtitleEn: enTranslation?.subtitle || '',
        contentEn: enTranslation?.content || '',
        titleRu: ruTranslation?.title || '',
        subtitleRu: ruTranslation?.subtitle || '',
        contentRu: ruTranslation?.content || ''
      });
    } else {
      setEditingNews(null);
      resetNewsForm();
    }
    setNewsDialog(true);
  };

  const resetNewsForm = () => {
    setNewsForm({
      slug: '',
      imageUrl: '',
      isPublished: true,
      publishedAt: new Date().toISOString().split('T')[0],
      titleEn: '',
      subtitleEn: '',
      contentEn: '',
      titleRu: '',
      subtitleRu: '',
      contentRu: ''
    });
  };

  // Handle image upload for news
  const handleNewsImageUpload = async (file: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('newsSlug', newsForm.slug || 'news');

      const response = await fetch('/api/news/admin/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload image');
      }

      return data.imageUrl;
    } catch (error: any) {
      console.error('Upload error:', error);
      throw new Error(error.message || 'Failed to upload image');
    }
  };

  const handleDeleteSubscriber = async (subscriberId: string) => {
    if (!window.confirm(t('admin.confirm.delete_subscriber'))) return;
    
    try {
      const response = await fetch(`/api/newsletter/admin/subscribers/${subscriberId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.subscriber_deleted'));
        fetchSubscribers();
      } else {
        setError(data.error || t('admin.error.failed_delete_subscriber'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleToggleSubscriberStatus = async (subscriberId: string, field: 'isConfirmed' | 'isActive', value: boolean) => {
    try {
      const response = await fetch(`/api/newsletter/admin/subscribers/${subscriberId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ [field]: value })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.subscriber_updated'));
        fetchSubscribers();
      } else {
        setError(data.error || t('admin.error.failed_update_subscriber'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleResendConfirmation = async (subscriberId: string) => {
    try {
      const response = await fetch(`/api/newsletter/admin/subscribers/${subscriberId}/resend-confirmation`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(t('admin.success.confirmation_sent'));
      } else {
        setError(data.error || t('admin.error.failed_send_confirmation'));
      }
    } catch (error) {
      setError(t('admin.error.network_error'));
    }
  };

  const handleGenerateNews = async () => {
    try {
      setGeneratingNews(true);
      setError('');
      setSuccess('');
      
      const response = await fetch('/api/news/admin/generate', {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('News generated successfully! Check the news list below.');
        // Refresh news list to show the new article
        await fetchNews();
      } else {
        setError(data.error || 'Failed to generate news');
      }
    } catch (error) {
      setError('Network error occurred while generating news');
    } finally {
      setGeneratingNews(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-white">{t("admin.loading")}</p>
        </div>
      </div>
    );
  }

  if (currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <div className="text-red-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-300">Admin privileges required to access this panel.</p>
        </div>
      </div>
    );
  }

  return (
          <div className="min-h-screen py-6">
              <div className="container mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Admin <span className="gradient-text">Panel</span>
          </h1>
          <p className="text-gray-300 text-lg">
            {t('admin.subtitle')}
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="glass-card border border-red-500/20 bg-red-500/10 p-4 mb-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-red-400 text-xl mr-3">⚠️</span>
                <span className="text-red-300">{error}</span>
              </div>
              <button 
                onClick={() => setError('')}
                className="text-red-400 hover:text-red-300 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="glass-card border border-green-500/20 bg-green-500/10 p-4 mb-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-green-400 text-xl mr-3">✅</span>
                <span className="text-green-300">{success}</span>
              </div>
              <button 
                onClick={() => setSuccess('')}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="glass-card p-1 mb-8 inline-flex rounded-xl">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'users'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            👥 {t('admin.user_management')}
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'products'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            🎵 {t('admin.product_management')}
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'subscribers'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            📧 {t('admin.newsletter_subscribers')}
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'news'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            📰 {t('admin.news_management')}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'orders'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            🧾 Orders
          </button>
          <button
            onClick={() => setActiveTab('entitlements')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'entitlements'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
            }`}
          >
            🔑 Entitlements
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search */}
            <div className="glass-card p-6">
              <div className="flex items-center space-x-4">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder={t('admin.search_users')}
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pl-12 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.name')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.email')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.role')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.created')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="p-4 text-white">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="p-4 text-gray-300">{user.email}</td>
                        <td className="p-4">
                          <select
                            value={user.role}
                            onChange={(e) => handleChangeUserRole(user._id, e.target.value)}
                            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                          >
                            <option value="user" className="bg-gray-800">{t('admin.role.user')}</option>
                            <option value="admin" className="bg-gray-800">{t('admin.role.admin')}</option>
                          </select>
                        </td>
                        <td className="p-4 text-gray-300">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2 rounded-lg transition-all duration-300"
                            title={t('admin.tooltip.delete_user')}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {userPagination.pages > 1 && (
                <div className="p-4 border-t border-white/10 flex justify-center">
                  <div className="flex space-x-2">
                    {Array.from({ length: userPagination.pages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setUserPage(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          userPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6 animate-fade-in">
            <div className="glass-card p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="status (pending/paid/refunded)" value={ordersFilter.status} onChange={(e)=>setOrdersFilter({...ordersFilter, status: e.target.value})} />
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="email" value={ordersFilter.email} onChange={(e)=>setOrdersFilter({...ordersFilter, email: e.target.value})} />
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="orderNumber" value={ordersFilter.orderNumber} onChange={(e)=>setOrdersFilter({...ordersFilter, orderNumber: e.target.value})} />
                <button onClick={fetchOrders} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold">Apply</button>
              </div>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-gray-300">Order #</th>
                      <th className="text-left p-4 text-gray-300">Email</th>
                      <th className="text-left p-4 text-gray-300">Total</th>
                      <th className="text-left p-4 text-gray-300">Status</th>
                      <th className="text-left p-4 text-gray-300">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o)=> (
                      <tr key={o._id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4 text-white font-mono text-sm">{o.orderNumber}</td>
                        <td className="p-4 text-gray-300">{o.billingInfo?.email}</td>
                        <td className="p-4 text-green-400">${o.totalAmount?.toFixed?.(2) ?? o.totalAmount}</td>
                        <td className="p-4"><span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm">{o.status}</span></td>
                        <td className="p-4 text-gray-300">{new Date(o.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ordersPagination.pages > 1 && (
                <div className="p-4 border-t border-white/10 flex justify-center">
                  <div className="flex space-x-2">
                    {Array.from({ length: ordersPagination.pages }, (_, i) => i + 1).map((page) => (
                      <button key={page} onClick={()=>setOrdersPage(page)} className={`px-4 py-2 rounded-lg ${ordersPage===page?'bg-gradient-to-r from-purple-600 to-pink-600 text-white':'bg-white/10 text-gray-300 hover:bg-white/20'}`}>{page}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Entitlements Tab */}
        {activeTab === 'entitlements' && (
          <div className="space-y-6 animate-fade-in">
            <div className="glass-card p-6">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="email" value={entitlementsFilter.email} onChange={(e)=>setEntitlementsFilter({...entitlementsFilter, email: e.target.value})} />
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="userId" value={entitlementsFilter.userId} onChange={(e)=>setEntitlementsFilter({...entitlementsFilter, userId: e.target.value})} />
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="orderId" value={entitlementsFilter.orderId} onChange={(e)=>setEntitlementsFilter({...entitlementsFilter, orderId: e.target.value})} />
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="subjectId" value={entitlementsFilter.subjectId} onChange={(e)=>setEntitlementsFilter({...entitlementsFilter, subjectId: e.target.value})} />
                <input className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white" placeholder="subjectType (product/collection)" value={entitlementsFilter.subjectType} onChange={(e)=>setEntitlementsFilter({...entitlementsFilter, subjectType: e.target.value})} />
                <button onClick={fetchEntitlements} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold">Apply</button>
              </div>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-gray-300">Email</th>
                      <th className="text-left p-4 text-gray-300">Subject</th>
                      <th className="text-left p-4 text-gray-300">Order</th>
                      <th className="text-left p-4 text-gray-300">Status</th>
                      <th className="text-left p-4 text-gray-300">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entitlements.map((e)=> (
                      <tr key={e._id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4 text-gray-300">{e.email || '-'}</td>
                        <td className="p-4 text-white font-mono text-sm">{e.subjectType}:{e.subjectId}</td>
                        <td className="p-4 text-gray-300 font-mono text-sm">{e.sourceOrderId}</td>
                        <td className="p-4"><span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm">{e.status}</span></td>
                        <td className="p-4 text-gray-300">{new Date(e.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {entitlementsPagination.pages > 1 && (
                <div className="p-4 border-t border-white/10 flex justify-center">
                  <div className="flex space-x-2">
                    {Array.from({ length: entitlementsPagination.pages }, (_, i) => i + 1).map((page) => (
                      <button key={page} onClick={()=>setEntitlementsPage(page)} className={`px-4 py-2 rounded-lg ${entitlementsPage===page?'bg-gradient-to-r from-purple-600 to-pink-600 text-white':'bg-white/10 text-gray-300 hover:bg-white/20'}`}>{page}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search and Add */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between space-x-4">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder={t('admin.search_products')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pl-12 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
                <button
                  onClick={() => openProductDialog()}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2"
                >
                  <span>➕</span>
                  <span>{t('admin.add_product')}</span>
                </button>
              </div>
            </div>

            {/* Products Table */}
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.name')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.category')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.price')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.version')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.status')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="p-4 text-white font-medium">{product.name}</td>
                        <td className="p-4">
                          <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm font-medium">
                            {product.category}
                          </span>
                        </td>
                        <td className="p-4 text-green-400 font-semibold">${product.price}</td>
                        <td className="p-4 text-gray-300">{product.version}</td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            product.isActive 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {product.isActive ? t('admin.status.active') : t('admin.status.inactive')}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openProductDialog(product)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 p-2 rounded-lg transition-all duration-300"
                              title={t('admin.tooltip.edit_product')}
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product._id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2 rounded-lg transition-all duration-300"
                              title={t('admin.tooltip.delete_product')}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {productPagination.pages > 1 && (
                <div className="p-4 border-t border-white/10 flex justify-center">
                  <div className="flex space-x-2">
                    {Array.from({ length: productPagination.pages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setProductPage(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          productPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search and Filter */}
            <div className="glass-card p-6">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 md:space-x-4">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder={t('admin.search_subscribers')}
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pl-12 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
                <select
                  value={subscriberStatus}
                  onChange={(e) => setSubscriberStatus(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="all" className="bg-gray-800">All Subscribers</option>
                  <option value="confirmed" className="bg-gray-800">Confirmed</option>
                  <option value="pending" className="bg-gray-800">Pending</option>
                  <option value="inactive" className="bg-gray-800">Inactive</option>
                </select>
              </div>
            </div>

            {/* Subscribers Table */}
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 font-semibold text-gray-300">{t("admin.table.email")}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t("admin.table.status")}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t("admin.table.source")}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t("admin.table.subscribed")}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t("admin.table.confirmed")}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((subscriber) => (
                      <tr key={subscriber._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="p-4 text-white font-medium">{subscriber.email}</td>
                        <td className="p-4">
                          <div className="flex flex-col space-y-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              subscriber.isConfirmed && subscriber.isActive
                                ? 'bg-green-500/20 text-green-300' 
                                : !subscriber.isConfirmed && subscriber.isActive
                                ? 'bg-yellow-500/20 text-yellow-300'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {subscriber.isConfirmed && subscriber.isActive ? t('admin.status.active') : 
                               !subscriber.isConfirmed && subscriber.isActive ? t('admin.status.pending') : t('admin.status.inactive')}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs font-medium">
                            {subscriber.source}
                          </span>
                        </td>
                        <td className="p-4 text-gray-300 text-sm">
                          {new Date(subscriber.subscribedAt).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-gray-300 text-sm">
                          {subscriber.confirmedAt ? new Date(subscriber.confirmedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-1">
                            {!subscriber.isConfirmed && subscriber.isActive && (
                              <button
                                onClick={() => handleResendConfirmation(subscriber._id)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 p-2 rounded-lg transition-all duration-300 text-sm"
                                title={t('admin.tooltip.resend_confirmation')}
                              >
                                📧
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleSubscriberStatus(subscriber._id, 'isConfirmed', !subscriber.isConfirmed)}
                              className="text-green-400 hover:text-green-300 hover:bg-green-500/20 p-2 rounded-lg transition-all duration-300 text-sm"
                              title={subscriber.isConfirmed ? t('admin.tooltip.mark_unconfirmed') : t('admin.tooltip.mark_confirmed')}
                            >
                              {subscriber.isConfirmed ? '✅' : '❓'}
                            </button>
                            <button
                              onClick={() => handleToggleSubscriberStatus(subscriber._id, 'isActive', !subscriber.isActive)}
                              className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/20 p-2 rounded-lg transition-all duration-300 text-sm"
                              title={subscriber.isActive ? t('admin.tooltip.deactivate') : t('admin.tooltip.activate')}
                            >
                              {subscriber.isActive ? '🔇' : '🔊'}
                            </button>
                            <button
                              onClick={() => handleDeleteSubscriber(subscriber._id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2 rounded-lg transition-all duration-300 text-sm"
                              title={t('admin.tooltip.delete_subscriber')}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {subscriberPagination.pages > 1 && (
                <div className="p-4 border-t border-white/10 flex justify-center">
                  <div className="flex space-x-2">
                    {Array.from({ length: subscriberPagination.pages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setSubscriberPage(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          subscriberPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search and Add */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between space-x-4">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder={t('admin.search_news')}
                    value={newsSearch}
                    onChange={(e) => setNewsSearch(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pl-12 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleGenerateNews}
                    disabled={generatingNews}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2"
                  >
                    {generatingNews ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <span>🤖</span>
                        <span>Generate News</span>
                      </>
                    )}
                  </button>
                <button
                  onClick={() => openNewsDialog()}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2"
                >
                  <span>➕</span>
                  <span>{t('admin.add_news')}</span>
                </button>
                </div>
              </div>
            </div>

            {/* News Table */}
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.title')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.slug')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.languages')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.published')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.status')}</th>
                      <th className="text-left p-4 font-semibold text-gray-300">{t('admin.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {news.map((newsItem) => {
                      const enTranslation = newsItem.translations.find(t => t.language === 'en');
                      const languages = newsItem.translations.map(t => t.language.toUpperCase()).join(', ');
                      
                      return (
                        <tr key={newsItem._id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="p-4 text-white font-medium">
                            {enTranslation?.title || newsItem.translations[0]?.title || 'No title'}
                          </td>
                          <td className="p-4 text-gray-300 font-mono text-sm">{newsItem.slug}</td>
                          <td className="p-4">
                            <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm font-medium">
                              {languages}
                            </span>
                          </td>
                          <td className="p-4 text-gray-300">
                            {new Date(newsItem.publishedAt).toLocaleDateString()}
                          </td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              newsItem.isPublished 
                                ? 'bg-green-500/20 text-green-300' 
                                : 'bg-yellow-500/20 text-yellow-300'
                            }`}>
                              {newsItem.isPublished ? t('admin.status.published') : t('admin.status.draft')}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openNewsDialog(newsItem)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 p-2 rounded-lg transition-all duration-300"
                                title={t('admin.tooltip.edit_news')}
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteNews(newsItem._id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2 rounded-lg transition-all duration-300"
                                title={t('admin.tooltip.delete_news')}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {newsPagination.pages > 1 && (
                <div className="p-4 border-t border-white/10 flex justify-center">
                  <div className="flex space-x-2">
                    {Array.from({ length: newsPagination.pages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setNewsPage(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          newsPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Product Dialog */}
        {productDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900/95 backdrop-blur-lg border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingProduct ? t('admin.dialog.edit_product') : t('admin.dialog.add_product')}
                </h2>
                <button
                  onClick={() => setProductDialog(false)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.name")} *</label>
                  <input
                    type="text"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder={t('admin.form.product_name_placeholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.description")} *</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder={t('admin.form.product_description_placeholder')}
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.price")} *</label>
                    <input
                      type="number"
                      value={productForm.price}
                      onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors disabled:opacity-60"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                      readOnly={priceComesFromStripe(editingProduct)}
                      disabled={priceComesFromStripe(editingProduct)}
                    />
                    {priceComesFromStripe(editingProduct) && (
                      <p className="text-gray-400 text-xs mt-2">Price is set in the Stripe Dashboard. Lookup key: {editingProduct?.slug}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.category")}</label>
                    <select
                      value={productForm.category}
                      onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    >
                      <option value="synthesizer" className="bg-gray-800">{t("admin.category.synthesizer")}</option>
                      <option value="effect" className="bg-gray-800">{t("admin.category.effect")}</option>
                      <option value="sampler" className="bg-gray-800">{t("admin.category.sampler")}</option>
                      <option value="utility" className="bg-gray-800">{t("admin.category.utility")}</option>
                      <option value="other" className="bg-gray-800">{t("admin.category.other")}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.version")}</label>
                  <input
                    type="text"
                    value={productForm.version}
                    onChange={(e) => setProductForm({ ...productForm, version: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="1.0.0"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.image_url")}</label>
                  <input
                    type="url"
                    value={productForm.imageUrl}
                    onChange={(e) => setProductForm({ ...productForm, imageUrl: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.download_url")}</label>
                  <input
                    type="url"
                    value={productForm.downloadUrl}
                    onChange={(e) => setProductForm({ ...productForm, downloadUrl: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="https://example.com/download.zip"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={() => setProductDialog(false)}
                  className="px-6 py-3 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 hover:text-white transition-all duration-300 font-medium"
                >
                  {t('admin.button.cancel')}
                </button>
                <button
                  onClick={handleProductSubmit}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all duration-300 font-medium"
                >
                  {editingProduct ? t('admin.button.update') : t('admin.button.create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* News Dialog */}
        {newsDialog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] animate-fade-in">
            <div className="glass-card p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900/95 backdrop-blur-lg border border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingNews ? t('admin.dialog.edit_news') : t('admin.dialog.add_news')}
                </h2>
                <button
                  onClick={() => setNewsDialog(false)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.slug")} *</label>
                    <input
                      type="text"
                      value={newsForm.slug}
                      onChange={(e) => setNewsForm({ ...newsForm, slug: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder={t('admin.form.news_slug_placeholder')}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.published_at")}</label>
                    <input
                      type="date"
                      value={newsForm.publishedAt}
                      onChange={(e) => setNewsForm({ ...newsForm, publishedAt: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>
                </div>

                {/* News Image Upload */}
                <NewsImageUpload
                  currentImageUrl={newsForm.imageUrl}
                  onImageChange={(newImageUrl) => setNewsForm({ ...newsForm, imageUrl: newImageUrl })}
                  onImageUpload={handleNewsImageUpload}
                  newsSlug={newsForm.slug}
                  disabled={loading}
                />

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPublished"
                    checked={newsForm.isPublished}
                    onChange={(e) => setNewsForm({ ...newsForm, isPublished: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="isPublished" className="text-gray-300 font-medium">
                    {t("admin.form.is_published")}
                  </label>
                </div>

                                 {/* English Translation */}
                 <div className="border border-white/20 rounded-lg p-4 bg-white/5">
                   <h3 className="text-lg font-semibold text-white mb-4">🇺🇸 English</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.title_en")} *</label>
                      <input
                        type="text"
                        value={newsForm.titleEn}
                        onChange={(e) => setNewsForm({ ...newsForm, titleEn: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder={t('admin.form.news_title_placeholder')}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.subtitle_en")} *</label>
                      <input
                        type="text"
                        value={newsForm.subtitleEn}
                        onChange={(e) => setNewsForm({ ...newsForm, subtitleEn: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder={t('admin.form.news_subtitle_placeholder')}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.content_en")} *</label>
                      <textarea
                        value={newsForm.contentEn}
                        onChange={(e) => setNewsForm({ ...newsForm, contentEn: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder={t('admin.form.news_content_placeholder')}
                        rows={6}
                        required
                      />
                    </div>
                  </div>
                </div>

                                 {/* Russian Translation */}
                 <div className="border border-white/20 rounded-lg p-4 bg-white/5">
                   <h3 className="text-lg font-semibold text-white mb-4">🇷🇺 Русский</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.title_ru")}</label>
                      <input
                        type="text"
                        value={newsForm.titleRu}
                        onChange={(e) => setNewsForm({ ...newsForm, titleRu: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder={t('admin.form.news_title_placeholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.subtitle_ru")}</label>
                      <input
                        type="text"
                        value={newsForm.subtitleRu}
                        onChange={(e) => setNewsForm({ ...newsForm, subtitleRu: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder={t('admin.form.news_subtitle_placeholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium">{t("admin.form.content_ru")}</label>
                      <textarea
                        value={newsForm.contentRu}
                        onChange={(e) => setNewsForm({ ...newsForm, contentRu: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                        placeholder={t('admin.form.news_content_placeholder')}
                        rows={6}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={() => setNewsDialog(false)}
                  className="px-6 py-3 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 hover:text-white transition-all duration-300 font-medium"
                >
                  {t('admin.button.cancel')}
                </button>
                <button
                  onClick={handleNewsSubmit}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all duration-300 font-medium"
                >
                  {editingNews ? t('admin.button.update') : t('admin.button.create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="glass-card p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-white">{t("admin.loading")}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanelC; 